import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormHelperText,
  Box,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onSubmit: (phone: string) => Promise<void>  // parent performs API call; throw on error
 * - initialPhone?: string
 * - hasExisting?: boolean   // show "you'll have to re-verify" note
 * - companyName?: string    // optional, used in consent copy; defaults to "our"
 */
const EditPhoneDialog = ({
  open,
  onClose,
  onSubmit,
  initialPhone = "",
  allowTexting = true,
  hasExisting = false,
  companyName = "Outliers ACT Prep",
}) => {
  const [phone, setPhone] = useState(initialPhone || "");
  const [phoneInvalid, setPhoneInvalid] = useState(false);
  const [consentChecked, setConsentChecked] = useState(allowTexting ?? true);
  const [showConsentError, setShowConsentError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setPhone(initialPhone || "");
      setPhoneInvalid(false);
      setErr(null);
      setLoading(false);
      setConsentChecked(allowTexting ?? true);
      setShowConsentError(false);
    }
  }, [open, initialPhone, allowTexting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone || phoneInvalid || loading) return;

    if (!consentChecked) {
      setShowConsentError(true);
      return;
    }

    setLoading(true);
    setErr(null);
    try {
      await onSubmit(phone.replace(/\s/g, ""), consentChecked);
    } catch (error) {
      const msg =
        (error && (error.reason || error.message)) ||
        "We experienced an error.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>
          {hasExisting ? "Edit Phone Number" : "Add Phone Number"}
        </DialogTitle>
        <DialogContent>
          <MuiTelInput
            fullWidth
            required
            error={phoneInvalid}
            helperText={phoneInvalid ? "Please enter a valid phone number" : ""}
            defaultCountry="US"
            onlyCountries={["CA", "US"]}
            variant="standard"
            label="Phone Number"
            value={phone}
            onChange={(val) => {
              if (err) setErr(null);
              setPhone(val);
              setPhoneInvalid(!matchIsValidTel(val));
            }}
          />

          <Box mt={2}>
            {hasExisting && (
              <Typography variant="subtitle2" my={2}>
                <strong>Note:</strong> If you change your phone number, you will
                have to re-verify it.
              </Typography>
            )}
            <FormControl
              required
              error={showConsentError && !consentChecked}
              component="fieldset"
              variant="standard"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentChecked}
                    onChange={(e) => {
                      setConsentChecked(e.target.checked);
                      if (showConsentError) setShowConsentError(false);
                    }}
                    name="smsConsent"
                  />
                }
                label={
                  <Typography variant="body2">
                    I consent to receive{" "}
                    <strong>automated, transactional</strong> SMS messages from{" "}
                    {companyName} services (e.g., check-in reminders, schedule
                    updates). No promotional messages. Message &amp; data rates
                    may apply. Reply <strong>STOP</strong> to stop at any time.
                    Reply <strong>HELP</strong> for help. If you previously
                    unsubscribed with STOP, reply <strong>START</strong> to
                    resume.
                  </Typography>
                }
              />
              {showConsentError && !consentChecked && (
                <FormHelperText>
                  You must provide consent to receive transactional SMS.
                </FormHelperText>
              )}
            </FormControl>
          </Box>

          <Typography
            variant="caption"
            display="block"
            mt={1}
            color="text.secondary"
          >
            You can revoke consent at any time by replying {'"'}STOP{'"'} or by
            unchecking {'"'}Allow texting{'"'} in your Notifications
            preferences. If messages were stopped and you’d like to resume,
            reply START to our number.
          </Typography>

          {err && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {err}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Close
          </Button>
          <LoadingButton
            type="submit"
            disabled={!phone || phoneInvalid || loading || !consentChecked}
            loading={loading}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditPhoneDialog;
